/**
 * The landing page's TV, as data.
 *
 * The front door sells one thing — a table on the big screen — so it shows one
 * rather than describing it: a bezel with a felt inside, cycling the five games
 * and drawing each with the same components the real TV uses. None of it is
 * live, so all of it is scripted here: the hands, the pockets and the rolls are
 * fixed rather than random, because a landing page that deals a different hand
 * on every reload has no picture of itself and nothing that can be asserted.
 *
 * The rule the scripts follow: every scene shows the game doing the thing that
 * is worth watching. Blackjack draws to a made hand, hold'em is at a showdown,
 * the shooter makes their point. Nothing here is a losing table.
 */
import {
  EMPTY_STATS,
  DEFAULT_THEME,
  MAX_SEATS,
  tallyGamePoll,
  type BalanceSeries,
  type Card,
  type CasinoMemberView,
  type CasinoTheme,
  type CrapsBetId,
  type GameType,
  type LedgerTapeEntry,
  type MemberStatsView,
  type PublicGamePoll,
  type PublicGamePollVote,
  type PublicParty,
  type RoulettePocket,
  type RoundEntry,
} from '@tv-casino/shared';
import type { CrapsStack } from '../components/crapsFelt.js';
import { type RouletteStackMap, rouletteSpotKey } from '../components/rouletteFelt.js';

const card = (rank: Card['rank'], suit: Card['suit']): Card => ({ rank, suit });

/** One row of cards on the demo felt: whose it is, and what they hold. */
export interface ReelHand {
  /** The plate under the row — 'Dealer', 'Banker', 'The board'. */
  label: string;
  /** `undefined` is a face-down slot, exactly as it is in a real hand. */
  cards: readonly (Card | undefined)[];
  /** Fan the row casino-style rather than laying it out flat. */
  overlap?: boolean;
  /** The reading of the row: '20', 'Full house', 'Natural 9'. */
  value?: string;
}

/**
 * What the demo felt is drawing while one game holds the screen. `cards` is a
 * felt with hands on it; `layout` is a printed betting table — the roulette
 * and craps felts, drawn with the shipped layout components, with the wheel or
 * the dome standing beside them the way they do on the real TV.
 */
export type ReelFelt = 'cards' | 'layout';

/**
 * One of the two phones under the bezel. The page's whole claim is that the
 * screen is the table and the phone is the seat, so the phones are not
 * decoration: each one belongs to a player who is on the felt above it, holds
 * the cards that player holds, and is being asked the question that player's
 * turn actually asks.
 */
export interface ReelPhone {
  /** The seat, and therefore the colour it wears on the felt and the board. */
  seatIndex: number;
  name: string;
  /** What is left in their stack — the casino bankroll, not a table buy-in. */
  chips: number;
  /** Their own cards, on the games that deal to a seat. */
  hand?: readonly (Card | undefined)[];
  /** The chip rail they bet from, where the felt itself is the control. */
  rail?: readonly number[];
  /** What the phone is asking them, in the words the phone uses. */
  prompt: string;
  /** The buttons under it. The first reads as the lit one. */
  actions: readonly string[];
}

/** One game's turn on the landing page's TV. */
export interface ReelScene {
  game: GameType;
  /** What the game picker calls it, so the strip and the console agree. */
  name: string;
  /** The strip's hook: what this table feels like, in a handful of words. */
  hook: string;
  /** The line under the felt while this game is on screen. */
  blurb: string;
  /** The dealer's call, printed on the felt the way the TV prints it. */
  call: string;
  felt: ReelFelt;
  /** Card games only; empty for roulette and craps, which deal none. */
  hands: readonly ReelHand[];
  /** The two phones under the bezel, both sat at the table on it. */
  phones: readonly [ReelPhone, ReelPhone];
}

/** The rail every phone bets from, in the denominations the chip tray offers. */
const RAIL: readonly number[] = [5, 25, 100];

/**
 * The reel, in the order the strip prints it — the same order the game picker
 * and the owner's table list use, so a visitor who opens a casino finds the
 * five names where the front door put them.
 */
export const REEL: readonly ReelScene[] = [
  {
    game: 'blackjack',
    name: 'Blackjack',
    hook: 'Splits, doubles, a 16 to sweat',
    blurb:
      'Every seat plays its own hand against one dealer, and the sixteen is yours to live with. Side bets pay when the table is cold.',
    call: 'Dealer stands on 17 · Ada pays 3:2',
    felt: 'cards',
    hands: [
      { label: 'Dealer', cards: [card('9', 'S'), card('8', 'D')], value: '17' },
      { label: 'Ada', cards: [card('A', 'H'), card('K', 'S')], value: 'Blackjack' },
      { label: 'Bo', cards: [card('10', 'D'), card('6', 'C')], value: '16' },
    ],
    phones: [
      {
        seatIndex: 0,
        name: 'Ada',
        chips: 2_480,
        hand: [card('A', 'H'), card('K', 'S')],
        prompt: 'Blackjack, paid 3:2',
        actions: ['Next hand'],
      },
      {
        seatIndex: 1,
        name: 'Bo',
        chips: 1_760,
        hand: [card('10', 'D'), card('6', 'C')],
        prompt: 'Sixteen against a nine',
        actions: ['Stand', 'Hit', 'Double'],
      },
    ],
  },
  {
    game: 'holdem',
    name: "Texas Hold'em",
    hook: 'Blinds, bluffs, one showdown',
    blurb: "Real blinds, a real board, and a showdown the TV reads out card by card. Blame the river.",
    call: 'River · 120 to Bo',
    felt: 'cards',
    hands: [
      {
        label: 'The board',
        cards: [card('Q', 'H'), card('9', 'H'), card('4', 'H'), card('J', 'C'), card('2', 'H')],
      },
      { label: 'Ada', cards: [card('K', 'S'), card('Q', 'D')], overlap: true, value: 'Pair of queens' },
      { label: 'Bo', cards: [card('A', 'H'), card('7', 'H')], overlap: true, value: 'Flush, ace high' },
    ],
    phones: [
      {
        seatIndex: 0,
        name: 'Ada',
        chips: 2_480,
        hand: [card('K', 'S'), card('Q', 'D')],
        prompt: 'You bet 120 · Bo to act',
        actions: ['Waiting'],
      },
      {
        seatIndex: 1,
        name: 'Bo',
        chips: 1_760,
        hand: [card('A', 'H'), card('7', 'H')],
        prompt: 'Flush, ace high',
        actions: ['Raise 240', 'Call 120', 'Fold'],
      },
    ],
  },
  {
    game: 'baccarat',
    name: 'Baccarat',
    hook: 'Pick a side. That is the game.',
    blurb: 'Player, banker or tie. The coup deals itself while you argue about which side to back.',
    call: 'Player 9 · natural, no draw',
    felt: 'cards',
    hands: [
      { label: 'Player', cards: [card('4', 'D'), card('5', 'S')], value: 'Natural 9' },
      { label: 'Banker', cards: [card('10', 'C'), card('7', 'S')], value: '7' },
    ],
    phones: [
      {
        seatIndex: 0,
        name: 'Ada',
        chips: 2_480,
        rail: RAIL,
        prompt: '100 on the player, pays even',
        actions: ['Player', 'Banker', 'Tie'],
      },
      {
        seatIndex: 1,
        name: 'Bo',
        chips: 1_760,
        rail: RAIL,
        prompt: '50 on the banker',
        actions: ['Banker', 'Player', 'Tie'],
      },
    ],
  },
  {
    game: 'roulette',
    name: 'Roulette',
    hook: 'The wheel owes no favors',
    blurb: 'Drop chips anywhere on the layout, then the ball rides the track and picks a number. It picks for everyone at once.',
    call: 'No more bets',
    felt: 'layout',
    hands: [],
    phones: [
      {
        seatIndex: 0,
        name: 'Ada',
        chips: 2_480,
        rail: RAIL,
        prompt: '25 straight up on 17',
        actions: ['Done', 'Clear'],
      },
      {
        seatIndex: 1,
        name: 'Bo',
        chips: 1_760,
        rail: RAIL,
        prompt: 'Red, and the second dozen',
        actions: ['Done', 'Clear'],
      },
    ],
  },
  {
    game: 'craps',
    name: 'Craps',
    hook: 'The loudest table in the house',
    blurb: 'One shooter throws for the whole table, and when the point lands the room makes the noise. The box asks whether to press it.',
    call: 'The dice are out',
    felt: 'layout',
    hands: [],
    phones: [
      {
        seatIndex: 0,
        name: 'Ada',
        chips: 2_480,
        rail: RAIL,
        prompt: 'Six paid 35, press it?',
        actions: ['Same bet', 'Press it', 'Take it down'],
      },
      {
        seatIndex: 1,
        name: 'Bo',
        chips: 1_760,
        rail: RAIL,
        prompt: 'Your roll, shooter',
        actions: ['Roll'],
      },
    ],
  },
];


/**
 * The chips on the demo roulette felt: a straight-up, an outside bet, a dozen
 * and a column, spread across the four seats so the layout shows what it is
 * for — everyone's money on one table, each stack in its owner's colour.
 * `mine` is false throughout: the TV holds no seat, so nothing on it is any
 * one phone's to pull.
 */
export const ROULETTE_STACKS: RouletteStackMap = {
  [rouletteSpotKey('straight', 17)]: [{ seatIndex: 0, amount: 25, mine: false, name: 'Ada' }],
  [rouletteSpotKey('straight', 0)]: [{ seatIndex: 3, amount: 5, mine: false, name: 'Del' }],
  [rouletteSpotKey('red')]: [
    { seatIndex: 1, amount: 100, mine: false, name: 'Bo' },
    { seatIndex: 2, amount: 50, mine: false, name: 'Cy' },
  ],
  [rouletteSpotKey('dozen2')]: [{ seatIndex: 1, amount: 50, mine: false, name: 'Bo' }],
  [rouletteSpotKey('column2')]: [{ seatIndex: 2, amount: 25, mine: false, name: 'Cy' }],
  [rouletteSpotKey('even')]: [{ seatIndex: 3, amount: 25, mine: false, name: 'Del' }],
};

/** The point the demo craps felt's puck rides on. */
export const CRAPS_POINT = 5;

/**
 * The chips on the demo craps felt. The line and its odds are behind the
 * point, the box numbers are bought, and one hardway is up — the shape a
 * table takes a few rolls into a hand.
 */
export const CRAPS_STACKS: Partial<Record<CrapsBetId, CrapsStack[]>> = {
  pass: [
    { seatIndex: 0, amount: 50, mine: false, working: true, name: 'Ada' },
    { seatIndex: 1, amount: 25, mine: false, working: true, name: 'Bo' },
  ],
  passOdds: [{ seatIndex: 0, amount: 100, mine: false, working: true, name: 'Ada' }],
  place6: [{ seatIndex: 0, amount: 30, mine: false, working: true, name: 'Ada' }],
  place8: [{ seatIndex: 2, amount: 30, mine: false, working: true, name: 'Cy' }],
  field: [{ seatIndex: 3, amount: 10, mine: false, working: true, name: 'Del' }],
  hard10: [{ seatIndex: 1, amount: 5, mine: false, working: true, name: 'Bo' }],
};

/**
 * The marquee over the door: the house's running commentary, one line at a
 * time, looping. It sells nothing the page does not do — every claim on it is
 * one the floor below makes good on.
 */
export const TICKER: readonly string[] = [
  'Tonight: your living room is the floor',
  'The TV deals · the phones decide',
  'No app · no sign-in · no mercy',
  'Party mode · the TV changes tables for you',
  'Bring your own luck',
  'The board remembers every chip',
  'Shooters wanted · no experience required',
  'Tipping the dealer does nothing, it is a browser',
];

/** How long one game holds the screen before the reel moves on. */
export const REEL_MS = 6_000;

/**
 * One card in the floor list for a felt the demo TV does not carry yet: it
 * pitches the game in the house's words but never wears the "On air" light,
 * which only the five scripted scenes can claim.
 */
export interface FloorGame {
  game: GameType;
  name: string;
  hook: string;
  blurb: string;
}

/** The floor deals seven; the reel above tapes five. These are the other two. */
export const FLOOR_ONLY: readonly FloorGame[] = [
  {
    game: 'threecard',
    name: '3-Card Poker',
    hook: 'Three cards, one call',
    blurb:
      'Stay in against the dealer or let your ante go. The dealer needs queen-high to play, and Pair Plus pays whether the hand wins or not.',
  },
  {
    game: 'keno',
    name: 'Keno',
    hook: 'Mark the ticket, watch the board',
    blurb:
      'Pick up to ten numbers from eighty, then the house draws twenty. Catch more than your share and the ticket pays by the book.',
  },
];

/** Wrap any tick — including a negative one — onto a scene. */
export const reelAt = (tick: number): ReelScene =>
  REEL[((tick % REEL.length) + REEL.length) % REEL.length]!;

/** Where a game sits in the reel, for the strip's own highlight. */
export const reelIndexOf = (game: GameType): number => REEL.findIndex((s) => s.game === game);

/**
 * The scripted spins, in the order the demo wheel lands them: a black number,
 * the zero that beats every outside bet, a red one, then a repeat that proves
 * the wheel has no memory.
 */
export const ROULETTE_REEL: readonly RoulettePocket[] = [17, 0, 32, 5, 17, 26];

/** How long the demo ball spends in the air, and the gap between spins. */
export const SPIN_MS = 3_400;
export const SPIN_GAP_MS = 1_400;

/** The pocket spin `n` lands in. Spin 0 is the ball at rest, before anything. */
export const rouletteReelPocket = (n: number): RoulettePocket =>
  ROULETTE_REEL[(((n - 1) % ROULETTE_REEL.length) + ROULETTE_REEL.length) % ROULETTE_REEL.length]!;

/** One throw of the demo dice, and what the stickman calls it. */
export interface ReelRoll {
  dice: readonly [number, number];
  call: string;
}

/**
 * One scripted hand of craps, come-out to made point. It has to be a real
 * hand — the calls name the dice, so a seven on the come-out has to say
 * winner and the point has to be repeated before it is made, or anyone who
 * has stood at a table will read the dome as decoration.
 */
export const CRAPS_REEL: readonly ReelRoll[] = [
  { dice: [4, 3], call: 'Seven, winner on the come-out' },
  { dice: [3, 2], call: 'Five, the point is five' },
  { dice: [6, 2], call: 'Eight, the place bets pay' },
  { dice: [5, 5], call: 'Hard ten' },
  { dice: [1, 4], call: 'Five, the shooter makes the point' },
];

/** How long the dome holds a landed roll before the shooter throws again. */
export const ROLL_MS = 3_000;

/** The roll throw `n` shows. Throw 0 is the dome before the first toss. */
export const crapsReelRoll = (n: number): ReelRoll =>
  CRAPS_REEL[(((n - 1) % CRAPS_REEL.length) + CRAPS_REEL.length) % CRAPS_REEL.length]!;

/** One line of the night's board: who is up, in their seat's colour. */
export interface ReelStanding {
  seatIndex: number;
  name: string;
  chips: number;
  /** Chips won (positive) or lost (negative) over the night so far. */
  delta: number;
  /** The tables they sat at, in the order they sat. */
  tables: string;
}

/**
 * A night's standings, for the section that explains a casino bankroll. The
 * point of the panel is that one stack crosses every felt, so the notes name
 * different games on purpose.
 */
export const REEL_STANDINGS: readonly ReelStanding[] = [
  { seatIndex: 0, name: 'Ada', chips: 2_480, delta: 980, tables: 'blackjack, craps, roulette' },
  { seatIndex: 1, name: 'Bo', chips: 1_760, delta: 260, tables: "hold'em" },
  { seatIndex: 2, name: 'Cy', chips: 1_115, delta: -385, tables: 'roulette' },
  { seatIndex: 3, name: 'Del', chips: 940, delta: -560, tables: 'baccarat' },
];

/**
 * Ada's night, as the bankroll band's chip desk replays it: the same shapes
 * the REST tapes carry, one buy-in and three settled hands that walk the copy's
 * own path — a natural at blackjack, a pass line lost behind the dice, then
 * pocket 17. Every figure reconciles with her row on the standings board:
 * bought in for 1,500, cashed out at 2,480, up 980.
 */
const ADA_AT = ['2026-09-12T20:04:00', '2026-09-12T20:12:00', '2026-09-12T20:31:00', '2026-09-12T20:58:00'] as const;
const ADA_ID = 'mem-ada-demo';
const ADA_ROUNDS = ['r-ada-3', 'r-ada-2', 'r-ada-1'] as const;

export const DEMO_MEMBER: Pick<
  CasinoMemberView,
  'memberId' | 'name' | 'chips' | 'buyIn'
> & { totals: MemberStatsView } = {
  memberId: ADA_ID,
  name: 'Ada',
  chips: 2_480,
  buyIn: 1_500,
  totals: {
    ...EMPTY_STATS,
    hands: 3,
    wins: 2,
    losses: 1,
    blackjacks: 1,
    net: 980,
    rounds: 3,
    wagered: 180,
    returned: 1_160,
    biggestWin: 880,
    biggestLoss: 50,
    bestRound: 880,
    worstRound: -50,
    winStreak: 1,
    bestWinStreak: 1,
    worstLossStreak: 1,
    straightUpHits: 1,
    sevenOuts: 1,
    rollsShot: 5,
    firstPlayedAt: `${ADA_AT[1]}Z`,
    lastPlayedAt: `${ADA_AT[3]}Z`,
  },
};

/** Ada's balance line: the buy-in, then one end-of-round point per hand. */
export const DEMO_SERIES: BalanceSeries = {
  memberId: ADA_ID,
  name: 'Ada',
  points: [
    {
      at: Date.parse(`${ADA_AT[0]}Z`),
      balance: 1_500,
      roundId: null,
      kind: 'buy_in',
      gameType: null,
    },
    {
      at: Date.parse(`${ADA_AT[1]}Z`),
      balance: 1_650,
      roundId: ADA_ROUNDS[2],
      kind: 'payout',
      gameType: 'blackjack',
    },
    {
      at: Date.parse(`${ADA_AT[2]}Z`),
      balance: 1_600,
      roundId: ADA_ROUNDS[1],
      kind: 'bet',
      gameType: 'craps',
    },
    {
      at: Date.parse(`${ADA_AT[3]}Z`),
      balance: 2_480,
      roundId: ADA_ROUNDS[0],
      kind: 'payout',
      gameType: 'roulette',
    },
  ],
  current: 2_480,
  peak: 2_480,
  trough: 1_500,
};

/**
 * Her last three hands, newest first, as the trades read them: the labels and
 * notes use the server's own grammar, so the desk carries the felt that
 * decided each one — the cards, the dice, the pocket.
 */
export const DEMO_ROUNDS: readonly RoundEntry[] = [
  {
    id: `${ADA_ROUNDS[0]}-${ADA_ID}`,
    roundId: ADA_ROUNDS[0],
    tableId: 'tbl-wheel',
    gameType: 'roulette',
    memberId: ADA_ID,
    seatIndex: 0,
    outcome: 'win',
    stake: 30,
    payout: 910,
    net: 880,
    bets: [
      { id: 'straight_up', label: 'Straight up 17', stake: 25, payout: 900 },
      { id: 'red', label: 'Red', stake: 5, payout: 10 },
    ],
    cards: [],
    note: 'Pocket 17 (red)',
    at: `${ADA_AT[3]}Z`,
  },
  {
    id: `${ADA_ROUNDS[1]}-${ADA_ID}`,
    roundId: ADA_ROUNDS[1],
    tableId: 'tbl-dome',
    gameType: 'craps',
    memberId: ADA_ID,
    seatIndex: 0,
    outcome: 'loss',
    stake: 50,
    payout: 0,
    net: -50,
    bets: [{ id: 'pass', label: 'Pass line — lose', stake: 50, payout: 0 }],
    cards: [],
    note: 'Seven out — 5 rolls, last 3-4',
    at: `${ADA_AT[2]}Z`,
  },
  {
    id: `${ADA_ROUNDS[2]}-${ADA_ID}`,
    roundId: ADA_ROUNDS[2],
    tableId: 'tbl-shoe',
    gameType: 'blackjack',
    memberId: ADA_ID,
    seatIndex: 0,
    outcome: 'blackjack',
    stake: 100,
    payout: 250,
    net: 150,
    bets: [{ id: 'main', label: 'main bet', stake: 100, payout: 250 }],
    cards: [
      {
        owner: 'player',
        cards: [card('A', 'H'), card('K', 'S')],
        label: 'blackjack',
      },
      { owner: 'dealer', cards: [card('9', 'D'), card('8', 'C')], label: '17' },
    ],
    note: null,
    at: `${ADA_AT[1]}Z`,
  },
];

/** The chip tape behind those hands, newest movement first. */
export const DEMO_LEDGER: readonly LedgerTapeEntry[] = [
  {
    id: 'lt-6',
    at: `${ADA_AT[3]}Z`,
    kind: 'payout',
    amount: 910,
    balanceAfter: 2_480,
    roundId: ADA_ROUNDS[0],
    tableId: 'tbl-wheel',
  },
  {
    id: 'lt-5',
    at: `${ADA_AT[3]}Z`,
    kind: 'bet',
    amount: -30,
    balanceAfter: 1_570,
    roundId: ADA_ROUNDS[0],
    tableId: 'tbl-wheel',
  },
  {
    id: 'lt-4',
    at: `${ADA_AT[2]}Z`,
    kind: 'bet',
    amount: -50,
    balanceAfter: 1_600,
    roundId: ADA_ROUNDS[1],
    tableId: 'tbl-dome',
  },
  {
    id: 'lt-3',
    at: `${ADA_AT[1]}Z`,
    kind: 'payout',
    amount: 250,
    balanceAfter: 1_650,
    roundId: ADA_ROUNDS[2],
    tableId: 'tbl-shoe',
  },
  {
    id: 'lt-2',
    at: `${ADA_AT[1]}Z`,
    kind: 'bet',
    amount: -100,
    balanceAfter: 1_400,
    roundId: ADA_ROUNDS[2],
    tableId: 'tbl-shoe',
  },
  {
    id: 'lt-1',
    at: `${ADA_AT[0]}Z`,
    kind: 'buy_in',
    amount: 1_500,
    balanceAfter: 1_500,
    roundId: null,
    tableId: null,
  },
];

export interface NightStep {
  title: string;
  body: string;
}

/**
 * How a night actually runs, start to finish. Each step is something the
 * product does today: the owner's sign-in, the join code, the TV dealing.
 */
export const NIGHT_STEPS: readonly NightStep[] = [
  {
    title: 'Put a table on the TV',
    body: 'Sign in, open the house, and put it on the TV: cast to a Chromecast or mirror to an Apple TV. The TV becomes the felt, the dealer and the scoreboard.',
  },
  {
    title: 'Hand out the code',
    body: 'The join code sits on the screen. Friends type it into their phones: no app, no sign-in.',
  },
  {
    title: 'Let it ride',
    body: 'The TV deals and calls every hand, the phones make the decisions, and every chip follows its player to the next table.',
  },
];

/**
 * Party mode's two ways to run a lineup, as the landing page tells them. Both
 * are what the host panel's Party mode section does today.
 */
export const PARTY_MODES: readonly NightStep[] = [
  {
    title: 'By rounds',
    body: 'Five hands of blackjack, five spins of roulette, five come-outs at craps, then round again. Set the count and the TV loops the lineup until you stop it.',
  },
  {
    title: 'On a schedule',
    body: 'Only have three hours? Pick the games and the length, and each one gets an equal slot. A slot that runs out mid-hand waits for the hand, and the night still ends on time.',
  },
];

/** A whole table look, named, for the landing page's quick picks. */
export interface HouseLook {
  name: string;
  theme: CasinoTheme;
}

/**
 * Finished looks a visitor can try on in one tap, each a felt, a chip set and
 * a deck that belong together. The first is the one every casino opens on, so
 * it doubles as the way back.
 */
export const HOUSE_LOOKS: readonly HouseLook[] = [
  { name: 'House', theme: DEFAULT_THEME },
  { name: 'Monte Carlo', theme: { felt: 'burgundy', chips: 'monaco', deck: 'royale' } },
  { name: 'High roller', theme: { felt: 'charcoal', chips: 'highroller', deck: 'gilded' } },
  { name: 'Jazz club', theme: { felt: 'mahogany', chips: 'jazz', deck: 'nocturne' } },
  { name: 'Neon strip', theme: { felt: 'magenta', chips: 'neon', deck: 'neonrose' } },
  { name: 'Terminal', theme: { felt: 'hacker', chips: 'hacker', deck: 'hacker' } },
];

/** The quick pick a theme matches exactly, if any — the one that wears the light. */
export const lookOf = (theme: CasinoTheme): HouseLook | undefined =>
  HOUSE_LOOKS.find(
    ({ theme: t }) => t.felt === theme.felt && t.chips === theme.chips && t.deck === theme.deck,
  );

/** The hand the dress-up felt deals: a card face down, then one of each suit's ink. */
export const DRESS_HAND: readonly (Card | undefined)[] = [
  undefined,
  card('A', 'S'),
  card('K', 'H'),
  card('Q', 'D'),
  card('J', 'C'),
];

const MINUTE = 60_000;

/**
 * The strip the TV shows under a three-hour, four-game schedule, 58:48 in:
 * blackjack has had its 45 minutes, roulette is on with 31:12 to go. Built
 * from `now` so the page's copy counts down like the real one.
 */
export function demoParty(now: number): PublicParty {
  const elapsed = 58 * MINUTE + 48_000;
  const startedAt = now - elapsed;
  return {
    mode: 'schedule',
    games: ['blackjack', 'roulette', 'craps', 'keno'],
    current: 'roulette',
    next: 'craps',
    slot: 1,
    lap: 1,
    laps: 1,
    roundsPerGame: null,
    roundsPlayed: null,
    slotEndsAt: startedAt + 90 * MINUTE,
    endsAt: startedAt + 180 * MINUTE,
    due: false,
    done: false,
  };
}

/** One figure on the rail under the hero: a number and what it counts. */
export interface HouseFact {
  value: number;
  label: string;
}

/**
 * The house in four numbers, for a visitor who reads nothing else. Each one is
 * counted off the thing it describes, so a new felt or a bigger table moves
 * the rail without anyone editing copy.
 */
export const HOUSE_FACTS: readonly HouseFact[] = [
  { value: REEL.length + FLOOR_ONLY.length, label: 'games on the floor' },
  { value: MAX_SEATS, label: 'seats at every table' },
  { value: 1, label: 'code to join' },
  { value: 0, label: 'apps to install' },
];

/**
 * The table vote the landing page runs. Ada opened it between hands at a
 * blackjack table; the other three ballots land one at a time and the visitor
 * holds the fifth. The ballot is in `GAME_TYPES` order, as a real one is.
 *
 * The script is built so the visitor's pick matters: craps leads 2-1 before
 * they vote, a vote for craps settles it, and a vote for blackjack ties it and
 * keeps the table dealing what it is dealing, which is the tie-break rule.
 */
export const DEMO_POLL_OPTIONS: readonly GameType[] = ['blackjack', 'roulette', 'craps', 'keno'];

/** The game the demo table is dealing when the vote opens. */
export const DEMO_POLL_CURRENT: GameType = 'blackjack';

/** The other seats' ballots, in the order they land. */
export const DEMO_BALLOTS: readonly PublicGamePollVote[] = [
  { playerId: 'demo-ada', name: 'Ada', game: 'blackjack' },
  { playerId: 'demo-bo', name: 'Bo', game: 'craps' },
  { playerId: 'demo-cy', name: 'Cy', game: 'keno' },
  { playerId: 'demo-del', name: 'Del', game: 'craps' },
];

/** The visitor's seat at the demo table. */
export const DEMO_VOTER_ID = 'demo-you';

/** The gap between one scripted ballot landing and the next. */
export const BALLOT_MS = 1_100;

/**
 * The poll as the room would publish it once `landed` of the scripted ballots
 * are in and the visitor has picked `mine` (or not yet). Tallied by the same
 * `tallyGamePoll` the server runs, so the tie-break is the real one.
 */
export function demoPoll(landed: number, mine: GameType | null): PublicGamePoll {
  const theirs = DEMO_BALLOTS.slice(0, Math.max(0, Math.min(landed, DEMO_BALLOTS.length)));
  const votes: PublicGamePollVote[] = mine
    ? [...theirs, { playerId: DEMO_VOTER_ID, name: 'You', game: mine }]
    : [...theirs];
  const { tallies, winner } = tallyGamePoll(DEMO_POLL_OPTIONS, votes, DEMO_POLL_CURRENT);
  return {
    options: [...DEMO_POLL_OPTIONS],
    tallies,
    votes,
    cast: votes.length,
    pending: DEMO_BALLOTS.length - theirs.length + (mine ? 0 : 1),
    openedBy: 'Ada',
    winner,
  };
}

/** One question a guest asks before they hand over their phone. */
export interface HouseQuestion {
  question: string;
  answer: string;
}

/**
 * The questions under the floor, answered in the house's voice. Every answer
 * is something the app does today: no roadmap, no maybes.
 */
export const HOUSE_QUESTIONS: readonly HouseQuestion[] = [
  {
    question: 'Does anyone have to download anything?',
    answer:
      'No. The TV side is a web page you cast or mirror, and players join in their phone browser with the code on the screen. Only whoever runs the house signs in.',
  },
  {
    question: 'What do I need for the TV?',
    answer:
      'A Chromecast or Google TV, an Apple TV over AirPlay, or any screen that can open a web page. A laptop on an HDMI cable works fine.',
  },
  {
    question: 'How many people can play?',
    answer: `${MAX_SEATS} seats a table. A casino can run more than one table, and each player's chips follow them from one to the next.`,
  },
  {
    question: 'Is this real money?',
    answer:
      'No. Chips are play money. They have no cash value and cannot be bought, sold or cashed out. The whole thing is free while it is in alpha.',
  },
  {
    question: 'What happens to the chips when the night ends?',
    answer:
      'In a casino they stay with each member, every hand on the ledger, until the house opens again. A quick table forgets everything the moment it closes.',
  },
];
